import React from 'react';
import { omit } from 'lodash';
import { CardElementData } from '@adyen/adyen-web/dist/types/components/Card/types';
import {
  CbObjOnFieldValid as FieldValid,
  CbObjOnError as CardError,
  CbObjOnFocus as CardFocus,
  CbObjOnBrand as CardBrand,
} from '@adyen/adyen-web/dist/types/components/internal/SecuredFields/lib/types';
import { AdyenCardType } from '../lib/adyen';
import { useFiveDigitLocale } from './user_five_digit_locale_hook';
import { adyenCheckout } from '../lib/adyen_checkout';
import { adyenEnvironment } from '../lib/adyenEnvironment';

export interface CardState {
  isValid: boolean; // Specifies if all the information that the shopper provided is valid.
  data: Partial<CardElementData>; // Provides the data that you need to pass in the `/payments` call.
}

export type CSEName =
 | 'encryptedCardNumber'
 | 'encryptedExpiryDate'
 | 'encryptedExpiryMonth'
 | 'encryptedExpiryYear'
 | 'encryptedSecurityCode';

// https://docs.adyen.com/payment-methods/cards/custom-card-integration#styling
const STYLES = {
  base: {
    background: 'transparent',
    color: '#212121',
    fontSize: '16px',
    fontSmoothing: 'auto',
    // eslint-disable-next-line max-len
    fontFamily: 'Consolas, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", Monaco, "Courier New", Courier, monospace',
    fontWeight: '400',
    lineHeight: '1.4',
    padding: '6px 8px',
  },
};

export interface AdyenCardResponse {
  focus: Partial<CardFocus>;
  errors: Record<string, Partial<CardError>>;
  cardState: CardState;
  endDigits: string;
  brand: string;
}

// Shared custom credit card hook (https://docs.adyen.com/payment-methods/cards/custom-card-integration)
export function useAdyenCard({ selector, cardType }: { selector: string; cardType: AdyenCardType }): AdyenCardResponse {
  const locale = useFiveDigitLocale();
  const { environment, clientKey } = adyenEnvironment;

  const [cardState, setCardState] = React.useState<CardState>({ isValid: false, data: { paymentMethod: { type: 'card' } } });
  const [focus, setFocus] = React.useState({});
  const [errors, setErrors] = React.useState({
    encryptedCardNumber: {},
    encryptedSecurityCode: {},
    encryptedExpiryMonth: {},
    encryptedExpiryYear: {},
  });
  const [endDigits, setEndDigits] = React.useState('');
  const [brand, setBrand] = React.useState('');

  React.useEffect(() => {
    const checkout = adyenCheckout({
      configuration: {
        clientKey,
        environment,
        locale,
        onChange: onChange(setCardState),
      },
    });

    checkout.create('securedfields', {
      type: cardType,
      brands: ['amex', 'cup', 'diners', 'discover', 'jcb', 'mc', 'visa'],
      styles: STYLES,
      onError: onError(setErrors),
      onFocus: onFocus(setFocus),
      onBrand: onBrand(setBrand),
      onFieldValid: onFieldValid({ setErrors, setEndDigits }),
    }).mount(selector);
  }, [
    locale,
    selector,
    environment,
    clientKey,
    cardType,
    setCardState,
    setFocus,
    setErrors,
    setEndDigits,
  ]);

  return { focus, errors, cardState, endDigits, brand };
}

function onFocus(setFocus) {
  return (event: CardFocus) => {
    setFocus(event);
  };
}

function onError(setErrors) {
  return (event: CardError) => {
    setErrors(previousErrors => ({ ...previousErrors, [event.fieldType]: event }));
  };
}

function onChange(setCardState) {
  return (state: CardState) => {
    setCardState(state);
  };
}

function onBrand(setBrand) {
  return (event: CardBrand) => {
    setBrand(event.brand);
  };
}

function onFieldValid({ setErrors, setEndDigits }) {
  return (event: FieldValid) => {
    setErrors(previousErrors => omit(previousErrors, event.fieldType));

    if (event.fieldType === 'encryptedCardNumber') {
      setEndDigits(event.endDigits);
    }
  };
}

export const callbacks = {
  onBrand,
  onChange,
  onError,
  onFieldValid,
  onFocus,
};
